import React from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '../context/AuthContext';
import { ChatProvider } from '../context/ChatContext';

// Layout
import MainLayout from '../layouts/MainLayout';

// Seiten
import Landing from '../pages/Landing';
import Home from '../pages/Home';
import CalendarPage from '../pages/CalendarPage';
import Settings from '../pages/Settings';
import UserProfile from '../pages/UserProfile';
import SearchPage from '../pages/SearchPage';
import TutorProfile from '../pages/TutorProfile';
import StudentProfile from '../pages/StudentProfile';
import ChatPage from '../pages/ChatPage';
import MeineAnfragen from '../pages/MeineAnfragen';
import AdminSkills from '../pages/AdminSkills';
import AdminTermine from '../pages/AdminTermine';
import AdminNutzer from '../pages/AdminNutzer';
import AdminTests from '../pages/AdminTests';

/**
 * ProtectedRoute: Leitet nicht-angemeldete Nutzer zur Landing Page weiter.
 */
function ProtectedRoute({ children }) {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? children : <Navigate to="/" replace />;
}

/**
 * AdminRoute: Leitet nicht-angemeldete oder Nicht-Admin-Nutzer zum Dashboard weiter.
 */
function AdminRoute({ children }) {
  const { isAuthenticated, user } = useAuth();
  return isAuthenticated && user?.role === 'admin' ? children : <Navigate to="/dashboard" replace />;
}

/**
 * PublicOnlyRoute: Leitet bereits angemeldete Nutzer direkt zum Dashboard.
 */
function PublicOnlyRoute({ children }) {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Landing Page — nur für nicht-angemeldete Nutzer */}
      <Route
        path="/"
        element={
          <PublicOnlyRoute>
            <Landing />
          </PublicOnlyRoute>
        }
      />

      {/* Geschützte Seiten — nur für angemeldete Nutzer */}
      <Route
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<Home />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/buchungen" element={<MeineAnfragen />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/profile" element={<UserProfile />} />
        <Route path="/suche" element={<SearchPage />} />
        <Route path="/tutor/:id" element={<TutorProfile />} />
        <Route path="/student/:id" element={<StudentProfile />} />
        <Route path="/chat/:tutorId" element={<ChatPage />} />

        {/* Admin-Bereich */}
        <Route
          path="/admin/skills"
          element={
            <AdminRoute>
              <AdminSkills />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/termine"
          element={
            <AdminRoute>
              <AdminTermine />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/nutzer"
          element={
            <AdminRoute>
              <AdminNutzer />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/tests"
          element={
            <AdminRoute>
              <AdminTests />
            </AdminRoute>
          }
        />
      </Route>

      {/* Fallback: alle unbekannten Routen zur Landing Page */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ChatProvider>
        <HashRouter>
          <AppRoutes />
        </HashRouter>
      </ChatProvider>
    </AuthProvider>
  );
}
